import React from 'react';
import { useSelector } from 'react-redux';

// Components

import AddressesInfo from '../../../../../components/ssap/pages/pageIncludes/summary/summaryIncludes/AddressesInfo';
import AuthorizedRepresentative from '../../../../../components/ssap/pages/pageIncludes/summary/summaryIncludes/AuthorizedRepresentative';
import ButtonContainer from '../../../../../containers/ssap/ButtonContainer';
import CommunicationChannelsInfo from '../../../../../components/ssap/pages/pageIncludes/summary/summaryIncludes/CommunicationChannelsInfo';
import EmergencyContactInfo from '../../../../../components/ssap/pages/pageIncludes/summary/summaryIncludes/EmergencyContactInfo';
import LanguageInfo from '../../../../../components/ssap/pages/pageIncludes/summary/summaryIncludes/LanguageInfo';
import SubHeader from '../../../../../components/ssap/headers/SubHeader';
import SummaryFilter from '../../../../../components/ssap/pages/pageIncludes/summary/SummaryFilter';
import SummaryItem from '../../../../../components/ssap/pages/pageIncludes/summary/SummaryItem';

// Selectors

import {
    getIsState,
    getSsapData,
    selectAddressVisibilityDueToGeneralDelivery,
    selectIfPageActiveByPageName,
    selectMailingAddressFromComPref,
    selectSsapPreviousData
} from '../../../../../reducers/appReducer';

// Utils

import { householdMemberTools } from '../../../../../utils/tools/householdMemberTools';
import { normalizeNameOfPerson } from '../../../../../utils/normalize/normalizeNameOfPerson';
import { toUTCAndFormat } from '../../../../../utils/dates/date.utils';

// Constants

import { authorizedRepresentativeTypes } from '../../../../../constants/ssap/hhmTypes/authorizedRepresentativeTypes.ts';

import {
    AUTHORIZED_REPRESENTATIVES_LIST,
    COMMUNICATION_METHODS_LIST,
    LANGUAGE_LIST
} from '../../../../../constants/ssap/hhmTypes/dropdownOptions';

import { PAGES } from '../../../../../constants/ssap/flows/pagesTypes';

// Types

import { SsapSummaryType } from '../../../../../components/ssap/SsapTypes';
import { MM_SLASH_DD_SLASH_YYYY } from '../../../../../utils/dates/dateFormats.utils';


const PrimaryContactPersonSummary = props => {

    const { indexMapper } = props;

    const data = useSelector(getSsapData);
    const isState = useSelector(getIsState);
    const previousData = useSelector(selectSsapPreviousData);
    const mailingAddressFromPortal = useSelector(selectMailingAddressFromComPref);
    const isFirstSectionSummaryPageActive = useSelector(
        ({ ssap }) => selectIfPageActiveByPageName(ssap, PAGES.HOUSEHOLD_SUMMARY_PAGE)
    );
    const isApplicationSummaryPageActive = useSelector(
        ({ ssap }) => selectIfPageActiveByPageName(ssap, PAGES.EXTRA_FINAL_APPLICATION_REVIEW_PAGE, ssap)
    );
    const addressVisibilityDueToGeneralDelivery = useSelector(selectAddressVisibilityDueToGeneralDelivery);

    const {
        applicantGuid,
        dateOfBirth,
        emergencyContactInfo,
        name,
        householdContact: {
            homeAddress,
            mailingAddress,
            phone: {
                phoneNumber,
                phoneExtension
            },
            otherPhone: {
                phoneNumber : _phoneNumber,
                phoneExtension: _phoneExtension
            },
            contactPreferences: {
                emailAddress,
                emailMe,
                preferredContactMethod,
                preferredSpokenLanguage,
                preferredWrittenLanguage,
                textMe
            }
        }
    } = householdMemberTools.getPrimaryContact(data.taxHousehold.householdMember);

    const {
        applyingForFinancialAssistanceIndicator,
        getHelpIndicator
    } = data;

    const pcpIndex = householdMemberTools.getPrimaryContactPersonIndex(previousData.taxHousehold.householdMember);

    const getDobValue = value => {
        return toUTCAndFormat(value, MM_SLASH_DD_SLASH_YYYY);
    };

    const getContactMethodFromList = contactMethod => {
        return COMMUNICATION_METHODS_LIST[contactMethod];
    };

    const getAuthorizedRepresentativeTypeFromList = type => {
        return (type === authorizedRepresentativeTypes.UNSPECIFIED || !type)
            ? 'common.no_text'
            : AUTHORIZED_REPRESENTATIVES_LIST[type];
    };

    const getLanguageFromList = language => {
        return language ? LANGUAGE_LIST[language] : 'common.no_text';
    };

    const getNotificationPreferences = (emailMe, textMe) => {
        switch (true) {
            case !!emailMe && !textMe:
                return 'common.email_me';
            case !emailMe && !!textMe:
                return 'common.text_me';
            default:
                return 'common.email_text_me';
        }
    };

    const customAddressItemProps = isFirstSectionSummaryPageActive
        ? [ 'addressItemClassesWithSidePaddings' ]
        : null;

    return (
        <SummaryFilter summaryId = { SsapSummaryType.PRIMARY_CONTACT_PERSON } indexMapper = { indexMapper } >
            <div className = 'summary__block margin-b-20' >
                <SubHeader
                    text = 'ssap.subheader.non_internationalized_text'
                    textReplacements = { {
                        text: normalizeNameOfPerson(name)
                    } }
                    headerClassName = 'gi-heading-alt'
                    additionalElement = {
                        isApplicationSummaryPageActive
                            ? null
                            : (
                                <ButtonContainer
                                    // @ts-ignore
                                    type = 'editButton_secondary'
                                    // @ts-ignore
                                    args = {
                                        isFirstSectionSummaryPageActive
                                            ? [ 'editPcp', 0 ]
                                            : [ undefined, PAGES.PRIMARY_CONTACT_INFORMATION_PAGE ]
                                    }
                                />
                            )
                    }
                    additionalElementClassName = 'header-btn'
                />
                <div className = 'grid-col-12 divider' />
                <div className = 'grid-row' >
                    <CommunicationChannelsInfo
                        { ...{
                            _phoneExtension,
                            _phoneNumber,
                            emailAddress,
                            phoneExtension,
                            phoneNumber,
                            pcpIndex
                        } }
                    />

                    <SummaryItem
                        textCode = 'common.dob'
                        value = { getDobValue(dateOfBirth) }
                        ignoreIntl
                        desc = {
                            [
                                [
                                    `taxHousehold.householdMember[${ pcpIndex }].dateOfBirth`,
                                    getDobValue
                                ]
                            ]
                        }
                    />
                </div>

                <AddressesInfo
                    { ... {
                        addressVisibilityDueToGeneralDelivery,
                        applicantGuid,
                        customAddressItemProps,
                        homeAddress,
                        isState,
                        mailingAddress,
                        mailingAddressFromPortal,
                        pcpIndex
                    } }
                />

                <div className = 'grid-row' >
                    <LanguageInfo
                        { ...{
                            isShown: !isState.MN,
                            languageDescriptors: [
                                [
                                    [
                                        `taxHousehold.householdMember[${ pcpIndex }].householdContact.contactPreferences.preferredSpokenLanguage`,
                                        getLanguageFromList
                                    ]
                                ],
                                [
                                    [
                                        `taxHousehold.householdMember[${ pcpIndex }].householdContact.contactPreferences.preferredWrittenLanguage`,
                                        getLanguageFromList
                                    ]
                                ]
                            ],
                            values: [
                                getLanguageFromList(preferredSpokenLanguage),
                                getLanguageFromList(preferredWrittenLanguage)
                            ]
                        } }
                    />

                    <SummaryItem
                        textCode = 'common.preferred_communication'
                        value = { getContactMethodFromList(preferredContactMethod) }
                        desc = {
                            [
                                [
                                    `taxHousehold.householdMember[${ pcpIndex }].householdContact.contactPreferences.preferredContactMethod`,
                                    getContactMethodFromList
                                ]
                            ]
                        }
                    />

                    {
                        (!!emailMe || !!textMe)

                            ? (
                                <SummaryItem
                                    textCode = 'common.preferred_notification'
                                    value = { getNotificationPreferences(emailMe, textMe) }
                                    desc = {
                                        [
                                            [
                                                [
                                                    `taxHousehold.householdMember[${ pcpIndex }].householdContact.contactPreferences.emailMe`,
                                                    `taxHousehold.householdMember[${ pcpIndex }].householdContact.contactPreferences.textMe`,
                                                ],
                                                getNotificationPreferences
                                            ]
                                        ]
                                    }
                                />
                            )
                            : null
                    }

                    {
                        !isState.MN
                            ? (
                                getHelpIndicator !== authorizedRepresentativeTypes.UNSPECIFIED
                                    ? (
                                        <SummaryItem
                                            textCode = 'common.help_applying_for_coverage'
                                            value = { getAuthorizedRepresentativeTypeFromList(getHelpIndicator) }
                                            desc = {
                                                [
                                                    [

                                                        'getHelpIndicator',
                                                        getAuthorizedRepresentativeTypeFromList

                                                    ]
                                                ]
                                            }
                                        />
                                    )
                                    : null
                            )
                            : null

                    }
                </div>

                {
                    isState.VA &&

                    <div className = 'grid-row'>
                        <EmergencyContactInfo
                            { ...{
                                emergencyContactInfo,
                                pcpIndex
                            } }
                        />
                    </div>
                }

                <AuthorizedRepresentative
                    { ...{
                        customAddressItemProps,
                        data,
                        isState
                    } }
                />

                {
                    !isState.MN &&

                    <div className = 'grid-row'>
                        <SummaryItem
                            textCode = 'common.help_paying_for_coverage'
                            value = { applyingForFinancialAssistanceIndicator }
                            desc = {
                                [
                                    [
                                        'applyingForFinancialAssistanceIndicator'
                                    ]
                                ]
                            }
                        />
                    </div>
                }
            </div>
        </SummaryFilter>
    );
};

export default PrimaryContactPersonSummary;